import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { last, orderBy } from 'lodash';
import { apiClient } from '../services/ApiClient';
import { type VesselTrackViewModel } from '../components/Map/types';
import { Timestamp } from '../services/Timestamp';
import { ApiEndpoints } from '../constants/api-constants';
import { Queries, QueryCacheConfig } from '../constants/query-constants';
import { type VesselPosition } from '../types';

const VesselTrackLimitInHours = 2;

export const useLiveVesselTracksData = (
  mmsi: string,
  operation: string,
  currentPosition: VesselPosition,
  enabled: boolean
): VesselTrackViewModel[] => {
  const [vesselTracks, setVesselTracks] = useState<VesselTrackViewModel[]>([]);

  const fetchLiveviewVesselTracks = async (): Promise<VesselTrackViewModel[]> => {
    return apiClient.get<VesselTrackViewModel[]>(ApiEndpoints.liveview.getVesselTracks(operation, mmsi));
  };

  const shouldQuery = !!mmsi && !!currentPosition && enabled;

  const { data } = useQuery<VesselTrackViewModel[]>({
    ...QueryCacheConfig[Queries.LiveVesselTracks],
    queryKey: [Queries.LiveVesselTracks, operation, mmsi],
    queryFn: fetchLiveviewVesselTracks,
    enabled: shouldQuery,
  });

  useEffect(() => {
    setVesselTracks(
      orderBy(data, x => x.timestamp).filter(
        t =>
          t.timestamp >= Timestamp.now().subtract(Timestamp.fromHours(VesselTrackLimitInHours)).toSeconds() &&
          t.timestamp <= currentPosition.timestamp
      )
    );
  }, [data]);

  useEffect(() => {
    if (vesselTracks?.length && currentPosition && last(vesselTracks)?.timestamp !== currentPosition?.timestamp) {
      setVesselTracks(tracks => {
        tracks.push(currentPosition);
        return [...tracks];
      });
    }
  }, [currentPosition]);

  return useMemo(() => {
    if (!vesselTracks?.length || !enabled) {
      return [];
    }

    return vesselTracks;
  }, [vesselTracks, enabled]);
};
